import * as THREE from 'three'
import type { RAPIER } from '../physics'

export type ElevatorState = 'bottom' | 'ascending' | 'top' | 'descending'

export interface ElevatorConfig {
  id: string
  position: THREE.Vector3
  size?: [number, number, number]
  travelDistance?: number
  speed?: number
  waitDelay?: number
}

export class ElevatorController {
  readonly id: string
  readonly position: THREE.Vector3
  readonly size: [number, number, number]
  readonly travelDistance: number
  readonly speed: number
  readonly waitDelay: number

  readonly bottomY: number
  readonly topY: number
  currentY: number
  state: ElevatorState = 'bottom'
  private waitTimer = 0

  mesh: THREE.Mesh | null = null
  body: import('@dimforge/rapier3d-compat').RigidBody | null = null

  constructor(config: ElevatorConfig) {
    this.id = config.id
    this.position = config.position.clone()
    this.size = config.size ?? [3, 0.4, 3]
    this.travelDistance = config.travelDistance ?? 5.0
    this.speed = config.speed ?? 2.0
    this.waitDelay = config.waitDelay ?? 2.0

    this.bottomY = this.position.y
    this.topY = this.bottomY + this.travelDistance
    this.currentY = this.bottomY
  }

  update(dt: number, isPlayerOnPlatform: boolean) {
    if (this.state === 'bottom') {
      if (isPlayerOnPlatform) {
        this.state = 'ascending'
        this.waitTimer = 0
      }
    } else if (this.state === 'ascending') {
      this.currentY = Math.min(this.topY, this.currentY + this.speed * dt)
      if (this.currentY >= this.topY) {
        this.state = 'top'
        this.waitTimer = 0
      }
    } else if (this.state === 'top') {
      this.waitTimer += dt
      if (this.waitTimer >= this.waitDelay) {
        this.state = 'descending'
      }
    } else if (this.state === 'descending') {
      this.currentY = Math.max(this.bottomY, this.currentY - this.speed * dt)
      if (this.currentY <= this.bottomY) {
        this.state = 'bottom'
        this.waitTimer = 0
      }
    }

    if (this.mesh) {
      this.mesh.position.y = this.currentY
    }
    if (this.body) {
      this.body.setNextKinematicTranslation({
        x: this.position.x,
        y: this.currentY,
        z: this.position.z,
      })
    }
  }
}

export function createElevatorInstance(
  scene: THREE.Scene,
  world: import('@dimforge/rapier3d-compat').World,
  RAPIER_REF: typeof RAPIER,
  config: ElevatorConfig,
): ElevatorController {
  const elevator = new ElevatorController(config)
  const [w, h, d] = elevator.size

  const geo = new THREE.BoxGeometry(w, h, d)
  const mat = new THREE.MeshStandardMaterial({
    color: 0x5a6578,
    metalness: 0.8,
    roughness: 0.25,
  })
  const mesh = new THREE.Mesh(geo, mat)
  mesh.position.copy(elevator.position)
  scene.add(mesh)
  elevator.mesh = mesh

  const bodyDesc = RAPIER_REF.RigidBodyDesc.kinematicPositionBased().setTranslation(
    elevator.position.x,
    elevator.position.y,
    elevator.position.z,
  )
  const body = world.createRigidBody(bodyDesc)
  world.createCollider(RAPIER_REF.ColliderDesc.cuboid(w * 0.5, h * 0.5, d * 0.5), body)
  elevator.body = body

  return elevator
}
